/**
 * My PA Orders (Postgres/Algolia) table column definitions and row mapping helpers.
 * Includes urology exception-flow header transforms aligned with the Medical PA Orders worklist.
 * DOS and creation dates from `_iso` fields use {@link formatDateToUTCWithFormat} so calendar days match UTC-backed API values in every viewer timezone.
 */
import { getQueryCommentsForMedicalPaOrder } from "../../../../api/firebase/firestoreService";
import {
  TableCellType,
  TableHeader,
} from "../../../../components/custom-table/table";
import { sortTableHeadersByOrder } from "../../../../components/custom-table/utils/sorting";
import { HOLD_TEXT_UNTIL } from "../../../../constants/stringLiterals";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { Hit } from "../../../../data-model/postgresResponseForMedicalPaOrders";
import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../enums/evBvWriteBackStatus";
import { getBadgePropsForAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import { MedicalPaOrderStatus } from "../../../../enums/medicalPaOrderStatus";
import {
  MedicalOrderReviewStatus,
  MedicalOrderStatus,
} from "../../../../enums/orderReviewStatus";
import { isExceptionFlowForUrologyOrg } from "../../../../enums/organizationTypes";
import { MyPaOrdersTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { formatDateToUTCWithFormat } from "../../../../utils/formatDate";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { rotateLastSegmentFirst } from "../../../../utils/getFullNameOfTheProvider";
import { capitalizeString } from "../../../../utils/stringModifications";
import { getOrderIcons } from "../../medicalPaOrdersAlgolia/utils/getOrderIcons";
import { MedicalPaOrdersAlgoliaSortFields } from "../../medicalPaOrdersAlgolia/utils/medicalPaOrdersTable";

/** Location column width for Sunstate My PA; funded by shrinking seven data columns by 1% each. */
const SUNSTATE_MY_PA_LOCATION_WIDTH = 7;

/**
 * Narrows core columns by 7% total so a new Location column (7%) can be inserted while both
 * RPA icon columns stay at full width and the table still totals 100%.
 */
const SUNSTATE_MY_PA_SHRUNK_WIDTHS: Partial<Record<string, number>> = {
  [MyPaOrdersTableColumnKeys.PATIENT_DETAILS]: 9,
  [MyPaOrdersTableColumnKeys.CREATED_AT]: 9,
  [MyPaOrdersTableColumnKeys.PLAN_DETAILS]: 9,
  [MyPaOrdersTableColumnKeys.MEDICATION]: 9,
  [MyPaOrdersTableColumnKeys.APPOINTMENT_TYPE]: 9,
  [MyPaOrdersTableColumnKeys.PROVIDER]: 10,
  [MyPaOrdersTableColumnKeys.DATE_OF_SERVICE]: 7,
  [MyPaOrdersTableColumnKeys.BO_VALUE]: 8,
};

/** Replaces the Medication column header with an Appointment Type column in the same slot. */
export const replaceMedicationWithAppointmentType = (
  header: TableHeader,
): TableHeader =>
  header.key === MyPaOrdersTableColumnKeys.MEDICATION
    ? {
        label: "Appointment Type",
        key: MyPaOrdersTableColumnKeys.APPOINTMENT_TYPE,
        order: header.order,
        width: header.width,
        type: TableCellType.STRING,
      }
    : header;

/**
 * For urology exception-flow orgs only: replaces the plain "BO Value" badge with the Estimate
 * clickable column used by {@link TableCellType.CLICKABLE_BADGE_WITH_ON_CLICK}.
 *
 * @param header - Original table header entry
 * @returns Estimate header when the slot was BO Value; otherwise unchanged
 */
const replaceBoValueWithEstimateForSunstateMyPa = (
  header: TableHeader,
): TableHeader =>
  header.key === MyPaOrdersTableColumnKeys.BO_VALUE
    ? {
        label: "Estimate",
        key: MyPaOrdersTableColumnKeys.BO_VALUE_WITH_RADIO_SELECT,
        order: header.order,
        width: header.width,
        type: TableCellType.CLICKABLE_BADGE_WITH_ON_CLICK,
      }
    : header;

/**
 * Applies {@link SUNSTATE_MY_PA_SHRUNK_WIDTHS} when the header key matches.
 *
 * @param header - Header to possibly resize
 * @returns Header with updated width when a shrink rule exists
 */
const applySunstateMyPaWidthShrink = (header: TableHeader): TableHeader => {
  const nextWidth = SUNSTATE_MY_PA_SHRUNK_WIDTHS[header.key];
  return nextWidth !== undefined ? { ...header, width: nextWidth } : header;
};

/**
 * Urology exception-flow My PA header pipeline: keeps both RPA columns, shrinks seven data columns
 * by 1% each to fund a Location column after Date of Creation, swaps Medication → Appointment Type,
 * and BO Value → Estimate. Column `order` values are reassigned contiguously (1-based).
 *
 * @param headers - Base {@link MyPaOrdersTableColumns} clone
 * @returns Transformed headers for exception-flow orgs
 */
const buildSunstateUrologyMyPaOrdersHeaders = (
  headers: TableHeader[],
): TableHeader[] => {
  const shrunk = headers.map(applySunstateMyPaWidthShrink);
  const withAppointmentType = shrunk.map(replaceMedicationWithAppointmentType);
  const withEstimate = withAppointmentType.map(
    replaceBoValueWithEstimateForSunstateMyPa,
  );
  const sorted = sortTableHeadersByOrder(withEstimate);
  const createdAtIndex = sorted.findIndex(
    (h) => h.key === MyPaOrdersTableColumnKeys.CREATED_AT,
  );
  if (createdAtIndex < 0) {
    return sorted.map((header, index) => ({ ...header, order: index + 1 }));
  }
  const locationHeader: TableHeader = {
    label: "Location",
    key: MyPaOrdersTableColumnKeys.LOCATION,
    order: -1,
    width: SUNSTATE_MY_PA_LOCATION_WIDTH,
    type: TableCellType.STRING,
  };
  const withInserted = [
    ...sorted.slice(0, createdAtIndex + 1),
    locationHeader,
    ...sorted.slice(createdAtIndex + 1),
  ];
  return withInserted.map((header, index) => ({
    ...header,
    order: index + 1,
  }));
};

export const MyPaOrdersTableColumns: TableHeader[] = [
  {
    label: "Patient Name",
    key: MyPaOrdersTableColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 10,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
    sortField: MedicalPaOrdersAlgoliaSortFields.FIRST_NAME,
  },
  {
    label: "Date of Creation",
    key: MyPaOrdersTableColumnKeys.CREATED_AT,
    order: 2,
    width: 10,
    sortable: true,
    type: TableCellType.DATE,
    sortField: MedicalPaOrdersAlgoliaSortFields.CREATED_AT,
  },
  {
    label: "Plan Details",
    key: MyPaOrdersTableColumnKeys.PLAN_DETAILS,
    order: 3,
    width: 10,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
    sortField: MedicalPaOrdersAlgoliaSortFields.PRIMARY_PAYER_NAME,
  },
  {
    label: "Medication",
    key: MyPaOrdersTableColumnKeys.MEDICATION,
    order: 4,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
    sortField: MedicalPaOrdersAlgoliaSortFields.REGIMEN_NAME,
  },
  {
    label: "Provider",
    key: MyPaOrdersTableColumnKeys.PROVIDER,
    order: 5,
    width: 11,
    sortable: true,
    type: TableCellType.STRING,
    sortField: MedicalPaOrdersAlgoliaSortFields.PRACTITIONER_NAME,
  },
  {
    label: "DOS",
    key: MyPaOrdersTableColumnKeys.DATE_OF_SERVICE,
    order: 6,
    width: 8,
    sortable: true,
    type: TableCellType.DATE,
    sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_SERVICE,
  },
  {
    label: "BO Value",
    key: MyPaOrdersTableColumnKeys.BO_VALUE,
    order: 7,
    width: 9,
    type: TableCellType.BADGE,
  },
  {
    label: "Auth Status",
    key: MyPaOrdersTableColumnKeys.AUTH_VERIFICATION,
    order: 8,
    width: 9,
    type: TableCellType.BADGE_WITH_STATUS_ICON,
  },
  {
    label: "Alerts",
    key: MyPaOrdersTableColumnKeys.ALERTS,
    order: 9,
    width: 6,
    type: TableCellType.TAG_WITH_ICON,
  },
  {
    label: "",
    key: MyPaOrdersTableColumnKeys.RPA_STATUS,
    order: 10,
    width: 4,
    type: TableCellType.RPA_STATUS,
  },
  {
    label: "",
    key: MyPaOrdersTableColumnKeys.RPA_STATUS_TWO,
    order: 11,
    width: 3,
    type: TableCellType.RPA_STATUS_TWO,
  },
  {
    label: "",
    key: MyPaOrdersTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS,
    order: 12,
    width: 7,
    type: TableCellType.OVERLAPPING_ICONS_WITH_POPOVER_AND_ACTIONS,
  },
  {
    label: "",
    key: MyPaOrdersTableColumnKeys.SIDE_MODAL_ICON,
    order: 13,
    width: 3,
    type: TableCellType.SIDE_MODAL_ICON,
  },
];

/**
 * Resolves My PA Orders Algolia table headers for the current org (exception-flow overrides).
 *
 * @param orgId - Organization id from {@link getOrgIdForFetchExternalWorklist}
 * @returns Header array whose widths sum to 100% before review-mode adjustment
 */
export const getMyPaOrdersAlgoliaTableHeaders = (
  orgId: string,
): TableHeader[] => {
  if (isExceptionFlowForUrologyOrg(orgId)) {
    return buildSunstateUrologyMyPaOrdersHeaders(MyPaOrdersTableColumns);
  }
  return MyPaOrdersTableColumns;
};

/**
 * Transforms raw Postgres hit records into table row objects consumable by CustomTable.
 * Maps patient details, dates, plan info, medication, auth status badges, RPA statuses,
 * alerts, and review/order icons into the My PA Orders column schema. The
 * `BO_VALUE_WITH_RADIO_SELECT` cell is backed by `estimate_value` so orgs that swap the
 * BO column to Estimate (exception-flow orgs) render the editable estimate amount.
 *
 * @param algoliaRecords - Array of hit records from the Postgres response
 * @param authStatusOptions - Available auth status options for badge rendering
 * @returns Array of table row data objects keyed by MyPaOrdersTableColumnKeys
 */
export const generateMyMedicalPaOrdersTableDataFromAlgoliaRecords = (
  algoliaRecords: Hit[],
  authStatusOptions: AuthStatusOptionModel[],
): MyMedicalPaOrdersTableData[] => {
  return algoliaRecords.map((algoliaRecord) => {
    const authStatus = getBadgePropsForAuthStatus(
      authStatusOptions,
      algoliaRecord?.auth_status,
    );

    const textToDisplay = `${HOLD_TEXT_UNTIL} ${formatDateToUTCWithFormat(algoliaRecord?.date_of_hold_until_iso ?? "", "MM/DD/YYYY", "--")}`;

    return {
      [MyPaOrdersTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(
            algoliaRecord?.first_name ?? "",
            "",
            algoliaRecord?.last_name ?? "",
          ),
        ),
        secondaryText: `MRN : ${algoliaRecord?.patient_id ?? ""}`,
      },
      [MyPaOrdersTableColumnKeys.DATE_OF_SERVICE]: formatDateToUTCWithFormat(
        algoliaRecord?.date_of_service_iso ?? "",
        "MM/DD/YYYY",
        "",
      ),
      [MyPaOrdersTableColumnKeys.PLAN_DETAILS]: {
        mainText: algoliaRecord?.primary_payer_name ?? "",
        secondaryText: algoliaRecord?.primary_member_id ?? "",
      },
      [MyPaOrdersTableColumnKeys.MEDICATION]: algoliaRecord?.regimen_name ?? "",
      [MyPaOrdersTableColumnKeys.APPOINTMENT_TYPE]:
        algoliaRecord?.regimen_name ?? "",

      [MyPaOrdersTableColumnKeys.ROW_DATA]: JSON.stringify(algoliaRecord),
      [MyPaOrdersTableColumnKeys.ID]: algoliaRecord?.id ?? "",
      [MyPaOrdersTableColumnKeys.CREATED_AT]: formatDateToUTCWithFormat(
        algoliaRecord?.created_at_iso ?? "",
        "MM/DD/YYYY",
        "",
      ),
      [MyPaOrdersTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus?.text ?? "",
        color: authStatus?.textColor ?? "",
        bgColor: authStatus?.bgColor ?? "",
        showInfoIcon:
          authStatus?.id === MedicalPaOrderStatus.HOLD ||
          authStatus?.id === MedicalPaOrderStatus.QUERY,
        displayText: textToDisplay,
        fetchFn:
          authStatus?.id === MedicalPaOrderStatus.QUERY
            ? () => getQueryCommentsForMedicalPaOrder(algoliaRecord?.id ?? "")
            : undefined,
        submissionStatus: algoliaRecord?.submission_status,
        orderId: algoliaRecord?.id ?? "",
      },
      [MyPaOrdersTableColumnKeys.PROVIDER]: rotateLastSegmentFirst(
        algoliaRecord?.practitioner_name ?? "",
      ),
      [MyPaOrdersTableColumnKeys.BO_VALUE]: {
        text: algoliaRecord?.bo_status ?? "Required",
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [MyPaOrdersTableColumnKeys.BO_VALUE_WITH_RADIO_SELECT]: {
        value: algoliaRecord?.estimate_value ?? "Required",
        id: algoliaRecord?.id ?? "",
      },
      [MyPaOrdersTableColumnKeys.LOCATION]: algoliaRecord?.location ?? "",
      [MyPaOrdersTableColumnKeys.ALERTS]: {
        alert_badges: algoliaRecord?.alert_badges ?? [],
        alerts: algoliaRecord?.alerts ?? [],
      },
      [MyPaOrdersTableColumnKeys.RPA_STATUS]: {
        status:
          algoliaRecord?.ev_write_back_status ??
          EVWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: algoliaRecord?.id ?? "",
        masterAuthStatus: algoliaRecord?.master_auth_status ?? "",
      },

      [MyPaOrdersTableColumnKeys.RPA_STATUS_TWO]: {
        status:
          algoliaRecord?.document_upload_status ??
          DocWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: algoliaRecord?.id ?? "",
        masterAuthStatus: algoliaRecord?.master_auth_status ?? "",
      },
      [MyPaOrdersTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS]: {
        ...getOrderIcons(
          algoliaRecord?.medical_order_status as MedicalOrderStatus,
          algoliaRecord?.medical_order_review_status as MedicalOrderReviewStatus,
          algoliaRecord?.master_auth_status === "query" &&
            algoliaRecord?.ai_agent_type === "agent",
        ),
        order_id: algoliaRecord?.id ?? "",
      },
      [MyPaOrdersTableColumnKeys.SIDE_MODAL_ICON]: {
        id: algoliaRecord?.id ?? "",
        status: "others",
        borderColor: algoliaRecord?.notes_color ?? "",
        isNotesPresent: algoliaRecord?.notes_present ?? false,
        patientId: algoliaRecord?.patient_id ?? "",
      },
    };
  });
};
